import { Box, Container, Typography, Link, Grid, Divider, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LoopIcon from '@mui/icons-material/Loop';

const productLinks = [
  { text: 'Features', path: '/features' },
  { text: 'Pricing', path: '/pricing' },
  { text: 'FAQ', path: '/faq' },
];

const companyLinks = [
  { text: 'About', path: '/about' },
];

const legalLinks = [
  { text: 'Terms of Service', path: '/terms' },
  { text: 'Privacy Policy', path: '/privacy' },
];

const highlights = [
  { icon: <AutoAwesomeIcon sx={{ fontSize: 15 }} />, text: 'AI-generated study loops' },
  { icon: <LoopIcon sx={{ fontSize: 15 }} />, text: 'Spaced repetition built in' },
];

function FooterLinkGroup({ title, links }: { title: string; links: { text: string; path: string }[] }) {
  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          color: 'text.disabled',
          fontWeight: 700,
          letterSpacing: 1.5,
          fontSize: '0.68rem',
          display: 'block',
          mb: 2,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {links.map((link) => (
          <Link
            key={link.text}
            component={RouterLink}
            to={link.path}
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              fontSize: '0.9rem',
              transition: 'color 0.2s ease',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            {link.text}
          </Link>
        ))}
      </Box>
    </Box>
  );
}

export function Footer() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(99,102,241,0.1)'}`,
        boxShadow: isDark
          ? 'inset 0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)'
          : 'inset 0 4px 24px rgba(99,102,241,0.07), inset 0 1px 0 rgba(255,255,255,0.9)',
      }}
    >
      <Container maxWidth="lg">
        {/* Main footer content */}
        <Box sx={{ pt: { xs: 7, md: 8 }, pb: { xs: 5, md: 6 } }}>
          <Grid container spacing={{ xs: 5, md: 4 }}>

            {/* Brand column */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                {/* Clay logo orb — matches nav */}
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                    boxShadow: [
                      '0 6px 16px rgba(99,102,241,0.45)',
                      '0 2px 6px rgba(0,0,0,0.14)',
                      'inset 0 2px 0 rgba(255,255,255,0.3)',
                      'inset 0 -2px 0 rgba(0,0,0,0.16)',
                    ].join(', '),
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 18, color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: 0.3 }}>
                  BrainLoop
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', lineHeight: 1.75, maxWidth: 300, mb: 3 }}
              >
                AI-powered flashcard study loops that help you master any subject faster.
                Loop it until you own it.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {highlights.map((h) => (
                  <Box key={h.text} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Clay highlight orbs */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                        boxShadow: [
                          '0 3px 8px rgba(99,102,241,0.4)',
                          '0 1px 3px rgba(0,0,0,0.12)',
                          'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                          'inset 0 -1.5px 0 rgba(0,0,0,0.14)',
                        ].join(', '),
                        color: 'white',
                      }}
                    >
                      {h.icon}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {h.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Spacer on desktop */}
            <Grid item xs={false} md={1} sx={{ display: { xs: 'none', md: 'block' } }} />

            {/* Product links */}
            <Grid item xs={6} sm={4} md={2}>
              <FooterLinkGroup title="Product" links={productLinks} />
            </Grid>

            {/* Company links */}
            <Grid item xs={6} sm={4} md={2}>
              <FooterLinkGroup title="Company" links={companyLinks} />
            </Grid>

            {/* Legal links */}
            <Grid item xs={6} sm={4} md={2}>
              <FooterLinkGroup title="Legal" links={legalLinks} />
            </Grid>

          </Grid>
        </Box>

        {/* Bottom bar */}
        <Divider />
        <Box
          sx={{
            py: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            © {new Date().getFullYear()} BrainLoop. All rights reserved.
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Built for learners, powered by AI.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
